import React, { useState, useEffect } from "react";
import { Textarea, Loader } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQuery } from "@apollo/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/sharp-regular-svg-icons";
import { gql } from "@/__generated__";
import InlineBanner from "./shared/InlineBanner";
import styles from "../styles/components/providerDefaultReferralMessage.module.scss";
import Tip from "@/components/Tip/Tip.tsx";

const GET_PROVIDER_AND_USER = gql(`
  query ProviderAndUserForDefaultMessage {
    provider {
      providerId
      providerName
      defaultReferralMessage
    }
    providerUser {
      role
    }
  }
`);

const SAVE_PROVIDER_DEFAULT_REFERRAL_MESSAGE = gql(`
  mutation SaveProviderDefaultReferralMessage($providerId: ID!, $message: String) {
    saveProviderDefaultReferralMessage(providerId: $providerId, message: $message) {
      providerId
      defaultReferralMessage
    }
  }
`);

type ReferralMessageForm = {
  message: string;
};

export const ProviderDefaultReferralMessage: React.FC = () => {
  const [messageSaveSuccess, setMessageSaveSuccess] = useState(false);

  const {
    data: providerData,
    loading: queryLoading,
    error: queryError,
  } = useQuery(GET_PROVIDER_AND_USER, {
    fetchPolicy: "no-cache",
  });

  const [saveMessage, { loading: saveLoading, error: saveError }] = useMutation(
    SAVE_PROVIDER_DEFAULT_REFERRAL_MESSAGE,
  );

  const { key, onSubmit, getInputProps, setValues } =
    useForm<ReferralMessageForm>({
      initialValues: {
        message: "",
      },
      validate: {
        message: (value) => {
          if (value && value.length > 2000) {
            return "Message must be 2000 characters or less";
          }
          return null;
        },
      },
    });

  useEffect(() => {
    if (providerData?.provider?.defaultReferralMessage) {
      setValues({
        message: providerData.provider.defaultReferralMessage,
      });
    }
  }, [providerData, setValues]);

  const saveReferralMessage = async (formData: ReferralMessageForm) => {
    setMessageSaveSuccess(false);

    const { message } = formData;

    await saveMessage({
      variables: {
        providerId: providerData?.provider?.providerId!,
        message: message.trim() || null,
      },
    });
    setMessageSaveSuccess(true);
  };

  if (queryLoading) {
    return null;
  }

  if (queryError || !providerData?.provider || !providerData?.providerUser) {
    return null;
  }

  if (providerData.providerUser.role !== "ADMIN") {
    return null;
  }

  return (
    <div className={styles.referralMessageSection}>
      <span className={styles.referralMessageHeader}>
        <FontAwesomeIcon icon={faEnvelope} />
        Patient referral message
      </span>
      {messageSaveSuccess && (
        <div className={styles.banner}>
          <InlineBanner
            type="success"
            message="Message saved successfully"
            key="messageSavedBanner"
          />
        </div>
      )}
      {saveError && (
        <div className={styles.banner}>
          <InlineBanner
            type="error"
            message={saveError.message}
            key={saveError.message}
          />
        </div>
      )}
      <Tip heading={"Note"} variant={"callout"} className={styles.tip}>
        This is the default message for the patient referral email. Editing this
        message will be change the default for all users in your organization.
      </Tip>

      <div className={styles.referralMessageForm}>
        <form
          id="saveMessage"
          className={styles.messageForm}
          onSubmit={onSubmit(saveReferralMessage)}
        >
          <Textarea
            placeholder="We've teamed up with Claimable to help fight insurance denials. Good news–we've already started an appeal for you! if you need any support along the way just reach out to Claimable at support@getclaimable.com. They're here to help!"
            key={key("message")}
            {...getInputProps("message")}
            classNames={{
              input: styles.messageTextarea,
              label: styles.messageLabel,
            }}
            minRows={4}
            maxRows={8}
            data-testid="referral-message-textarea"
          />
          <button
            className={`${styles.saveMessageButton} ${saveLoading ? "loading_button" : ""}`}
            type="submit"
            disabled={saveLoading}
            data-testid="save-message-button"
          >
            {saveLoading ? <Loader color="black" /> : "Save message"}
          </button>
        </form>
      </div>
    </div>
  );
};
